import { Stack, StackProps } from '@mui/material';
import { PropsWithChildren } from 'react';

import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';

export interface DiagramBarProps extends PropsWithChildren {
  spacing?: StackProps['spacing'];
  borderRadius?: string;
  /**
   * Horizontal padding override. Defaults to '3px' so button-filled bars hug
   * their buttons (which carry their own internal padding). Text-only bars
   * (e.g. the "Select tiles to edit" empty state) pass a larger value so the
   * text doesn't sit flush against the border.
   */
  paddingX?: string;
}

/**
 * Shared presentational container for bars that appear over diagrams.
 * Renders a white, bordered, shadowed horizontal bar.
 */
export function DiagramBar({
  children,
  spacing = 2,
  borderRadius = '4px',
  paddingX = '3px',
}: DiagramBarProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={spacing}
      sx={{
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        borderRadius,
        border: `1px solid ${COLORS.GRAY[200]}`,
        // 3px vertical padding matches `SlideToolbar` so button-filled bars hug
        // their buttons the same way. Fixed 36px height keeps text-only bars
        // (e.g. "Select tiles to edit") the same height as button-filled bars.
        py: '3px',
        px: paddingX,
        height: '36px',
        boxShadow: THEME_SHADOWS.lg,
        // Fixed-height bar: labels must never wrap.
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Stack>
  );
}
